import type { Passkey } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogIn, Plug, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import qrcode from 'qrcode-generator';
import { useMemo, useState, type FormEvent } from 'react';

import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { rememberSsoState, useAuth } from '@/lib/auth';
import { relativeTime } from '@/lib/format';
import { createPasskey, passkeysSupported } from '@/lib/webauthn';

/** Settings that belong to the signed-in person rather than to any project or organization. */
export function UserSettingsPage() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">User settings</h1>
        <p className="text-sm text-muted-foreground">
          Your account, how you sign in, and the applications you have connected.
        </p>
      </div>

      <Profile />
      <TwoFactor />
      <Passkeys />
      <ConnectedAccounts />
      <ConnectedApplications />
    </div>
  );
}

function Profile() {
  const { user } = useAuth();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="size-4" />
          Profile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <p className="text-sm">{user?.name}</p>
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <p className="text-sm">{user?.email}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * An authenticator app as a second factor.
 *
 * Enrolment is two steps because the server will not enable anything until a
 * code proves the app holds the secret — a QR code that silently failed to
 * register would otherwise only surface at the next sign-in, with the account
 * already locked.
 */
function TwoFactor() {
  const { client, upgradeSession } = useAuth();
  const queryClient = useQueryClient();

  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [confirming, setConfirming] = useState(false);

  const status = useQuery({ queryKey: ['mfa-status'], queryFn: () => client.mfaStatus() });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
    // Requiring MFA is decided per organization, and the answer may change.
    void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const start = useMutation({
    mutationFn: () => client.startTotpEnrolment(),
    onSuccess: () => {
      setCode('');
      setRecoveryCodes(null);
    },
  });

  const confirm = useMutation({
    mutationFn: () => client.confirmTotp(code),
    onSuccess: async (confirmed) => {
      // Confirming proves possession, so the session becomes a verified one
      // without a second sign-in.
      await upgradeSession(confirmed.session);
      setRecoveryCodes(confirmed.recovery_codes);
      setCode('');
      start.reset();
      refresh();
    },
  });

  const disable = useMutation({
    mutationFn: () => client.disableTotp(code),
    onSuccess: () => {
      setCode('');
      setConfirming(false);
      setRecoveryCodes(null);
      refresh();
    },
  });

  const regenerate = useMutation({
    mutationFn: () => client.regenerateRecoveryCodes(code),
    onSuccess: (result) => {
      setRecoveryCodes(result.recovery_codes);
      setCode('');
      setConfirming(false);
      refresh();
    },
  });

  const enabled = status.data?.totp_enabled ?? false;
  const error = confirm.error ?? disable.error ?? regenerate.error ?? start.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="size-4" />
          Authenticator app
        </CardTitle>
        <CardDescription>
          A six-digit code from an app on your phone, asked for after your password. Some
          organizations require this, or a passkey, before you can see their projects.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {status.isLoading && <ListSkeleton rows={1} />}
        {status.isError && (
          <ErrorState error={status.error} onRetry={() => void status.refetch()} />
        )}

        {status.data && (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={enabled ? 'secondary' : 'muted'}>{enabled ? 'On' : 'Off'}</Badge>
            {enabled && (
              <span className="text-xs text-muted-foreground">
                {status.data.recovery_codes_remaining} recovery{' '}
                {status.data.recovery_codes_remaining === 1 ? 'code' : 'codes'} left
              </span>
            )}

            <div className="ml-auto flex gap-2">
              {!enabled && !start.data && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => start.mutate()}
                  disabled={start.isPending}
                >
                  Set up
                </Button>
              )}
              {enabled && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setConfirming(true);
                      regenerate.reset();
                      disable.reset();
                    }}
                  >
                    New recovery codes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setConfirming(true);
                      disable.reset();
                      regenerate.reset();
                    }}
                  >
                    Turn off
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Enrolling: scan, then prove it worked. */}
        {start.data && !enabled && (
          <form
            className="space-y-3 rounded-md border border-border p-3"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              confirm.mutate();
            }}
          >
            <p className="text-sm">
              Scan this with your authenticator app, then enter the code it shows.
            </p>
            <QrCode value={start.data.otpauth_uri} />
            <div className="space-y-1.5">
              <Label htmlFor="totp-secret">Or enter this key by hand</Label>
              <code id="totp-secret" className="block break-all text-xs text-muted-foreground">
                {start.data.secret}
              </code>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="totp-code">Code from the app</Label>
              <Input
                id="totp-code"
                className="max-w-40"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                autoComplete="one-time-code"
                placeholder="123456"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={confirm.isPending}>
                Turn on
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => start.reset()}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Turning it off, or replacing the recovery codes, both need a code. */}
        {/* One code field, two things it can authorize, so this is not a form
            with a single submit. */}
        {enabled && confirming && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="space-y-1.5">
              <Label htmlFor="confirm-code">Enter a current code to confirm</Label>
              <Input
                id="confirm-code"
                className="max-w-40"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                autoComplete="one-time-code"
                placeholder="123456"
              />
              <p className="text-xs text-muted-foreground">A recovery code works here too.</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => regenerate.mutate()}
                disabled={!code || regenerate.isPending}
              >
                New recovery codes
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => disable.mutate()}
                disabled={!code || disable.isPending}
              >
                Turn off
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setConfirming(false);
                  setCode('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {recoveryCodes && <RecoveryCodes codes={recoveryCodes} />}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * The QR code for an enrolment.
 *
 * Rendered as a data URL rather than injected markup: the value encoded here
 * contains an email address, and nothing user-supplied should reach the DOM as
 * HTML when an image does the job.
 */
function QrCode({ value }: { value: string }) {
  const source = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    return qr.createDataURL(4, 2);
  }, [value]);

  return (
    <img
      src={source}
      alt="QR code for setting up your authenticator app"
      className="size-44 rounded bg-white p-2"
    />
  );
}

/** Shown once, at the moment they are issued. The server keeps only hashes. */
function RecoveryCodes({ codes }: { codes: string[] }) {
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <p className="text-sm font-medium">Save your recovery codes</p>
      <p className="text-xs text-muted-foreground">
        Each one signs you in once if you lose your phone. This is the only time they are shown.
      </p>
      <div className="grid grid-cols-2 gap-1 font-mono text-xs">
        {codes.map((code) => (
          <code key={code}>{code}</code>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}
      >
        Copy
      </Button>
    </div>
  );
}

/**
 * Passkeys.
 *
 * A passkey is both factors at once — the device, plus the fingerprint or PIN
 * that unlocked it — so signing in with one is never followed by a code.
 */
function Passkeys() {
  const { client, upgradeSession } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const passkeys = useQuery({ queryKey: ['passkeys'], queryFn: () => client.listPasskeys() });

  const add = useMutation({
    mutationFn: async () => {
      const options = await client.passkeyRegistrationOptions();
      return client.registerPasskey(name.trim(), await createPasskey(options));
    },
    onSuccess: async (registered) => {
      // As with an authenticator: the ceremony just proved the factor, so the
      // session is upgraded rather than the user asked to sign in again.
      await upgradeSession(registered.session);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      void queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const remove = useMutation({
    mutationFn: (passkeyId: string) => client.deletePasskey(passkeyId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
  });

  const supported = passkeysSupported();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="size-4" />
          Passkeys
        </CardTitle>
        <CardDescription>
          Sign in with your fingerprint, face or device PIN instead of a password. A passkey counts
          as two-factor authentication on its own, so you are never asked for a code as well, and it
          cannot be phished — it only works on this site.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {passkeys.isLoading && <ListSkeleton rows={1} />}
        {passkeys.isError && (
          <ErrorState error={passkeys.error} onRetry={() => void passkeys.refetch()} />
        )}

        {passkeys.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">You have no passkeys yet.</p>
        )}

        {passkeys.data?.map((passkey) => (
          <PasskeyRow
            key={passkey.id}
            passkey={passkey}
            busy={remove.isPending}
            onRemove={() => {
              if (window.confirm(`Remove ${passkey.name}? You can add it again later.`)) {
                remove.mutate(passkey.id);
              }
            }}
          />
        ))}

        {supported ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              add.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="passkey-name">Name</Label>
              <Input
                id="passkey-name"
                className="w-56"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Work laptop"
                required
              />
            </div>
            <Button type="submit" variant="outline" disabled={add.isPending || !name.trim()}>
              Add a passkey
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">This browser does not support passkeys.</p>
        )}

        {(add.error || remove.error) && (
          <p className="text-sm text-destructive">
            {((add.error ?? remove.error) as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PasskeyRow({
  passkey,
  busy,
  onRemove,
}: {
  passkey: Passkey;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium">{passkey.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          added {relativeTime(passkey.created_at)} ago
          {passkey.last_used_at
            ? ` · last used ${relativeTime(passkey.last_used_at)} ago`
            : ' · never used'}
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        title={`Remove ${passkey.name}`}
        onClick={onRemove}
        disabled={busy}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

/**
 * External accounts this user can sign in with.
 *
 * Connecting one from here is the way an established account adopts single
 * sign-on: it proves the same person holds both sides, which matching on an
 * email address alone does not — and at some providers cannot.
 */
function ConnectedAccounts() {
  const { client, sso } = useAuth();
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({ queryKey: ['auth-options'], queryFn: () => client.authOptions() });
  const identities = useQuery({
    queryKey: ['sso-identities'],
    queryFn: () => client.listSsoIdentities(),
  });

  const disconnect = useMutation({
    mutationFn: (provider: string) => client.disconnectSso(provider),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['sso-identities'] });
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not disconnect that account.'),
  });

  const connect = (provider: string) => {
    setConnecting(provider);
    setError(null);
    // Navigates away on success, so there is no resolved state to handle.
    void client
      .startSsoConnect(provider)
      .then(({ authorization_url, state }) => {
        rememberSsoState(state);
        window.location.assign(authorization_url);
      })
      .catch((caught: unknown) => {
        setConnecting(null);
        setError(caught instanceof Error ? caught.message : 'Could not start that sign-in.');
      });
  };

  const providers = options.data?.sso_providers ?? [];
  const connected = identities.data ?? [];

  // Providers appear here only once an organization accepts them; connecting to
  // one nobody accepts would be a credential that opens nothing.
  if (options.isSuccess && providers.length === 0 && connected.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <LogIn className="size-4" />
          Connected accounts
        </CardTitle>
        <CardDescription>
          Accounts you can sign in with instead of your email and password. Organizations that
          require single sign-on accept only these.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {identities.isLoading && <ListSkeleton rows={1} />}
        {identities.isError && (
          <ErrorState error={identities.error} onRetry={() => void identities.refetch()} />
        )}

        {providers.map((provider) => {
          const identity = connected.find((item) => item.provider === provider.id);
          return (
            <div
              key={provider.id}
              className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{provider.name}</span>
                  {sso === provider.id && <Badge variant="muted">this session</Badge>}
                </div>
                {identity && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {identity.email ?? 'connected'}
                    {identity.last_login_at
                      ? ` · last used ${relativeTime(identity.last_login_at)} ago`
                      : ' · never used'}
                  </span>
                )}
              </div>

              {identity ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => disconnect.mutate(provider.id)}
                  disabled={disconnect.isPending}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => connect(provider.id)}
                  disabled={connecting !== null}
                >
                  Connect
                </Button>
              )}
            </div>
          );
        })}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * Applications the user has connected over MCP. A grant covers every project
 * the user can see, which is why this is an account setting. Disconnecting
 * takes effect on the application's very next request.
 */
function ConnectedApplications() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  const applications = useQuery({
    queryKey: ['connected-applications'],
    queryFn: () => client.listConnectedApplications(),
  });

  const revoke = useMutation({
    mutationFn: (grantId: string) => client.revokeConnectedApplication(grantId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['connected-applications'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="size-4" />
          Connected applications
        </CardTitle>
        <CardDescription>
          AI assistants and other tools you have allowed to use your Outpost data. Access covers
          every project you can see, and changes are made in your name, never beyond your role.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {applications.isLoading && <ListSkeleton rows={2} />}
        {applications.isError && (
          <ErrorState error={applications.error} onRetry={() => void applications.refetch()} />
        )}

        {applications.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">No applications are connected.</p>
        )}

        {applications.data?.map((application) => (
          <div
            key={application.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-medium wrap-anywhere">{application.client_name}</span>
                {/* In words rather than scope names: what someone reviewing
                    this list needs to know is whether it can change things. */}
                {application.scopes.includes('outpost:write') ? (
                  <Badge variant="outline">can make changes</Badge>
                ) : (
                  <Badge variant="muted">read-only</Badge>
                )}
              </div>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                connected {relativeTime(application.created_at)} ago
                {application.last_used_at
                  ? ` · last used ${relativeTime(application.last_used_at)} ago`
                  : ' · never used'}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              title={`Disconnect ${application.client_name}`}
              onClick={() => revoke.mutate(application.id)}
              disabled={revoke.isPending}
            >
              <Trash2 />
            </Button>
          </div>
        ))}

        {revoke.error && (
          <p className="text-sm text-destructive">{(revoke.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}
